import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Result,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, EditOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton, Pagination, SearchCard, DemoSelect } from '@/widgets/demo/ListPrimitives'
import { computePagination, demoButtonClass } from '@/widgets/demo/listHelpers'
import { useAuth, useBackToParent, usePermission, usePersistentPageSize } from '@/hooks'
import useSearchPersistence from '@/hooks/useSearchPersistence'
import { useToast } from '@/widgets/common'
import { municipalitiesApi } from '@/services'
import type { MunicipalityResponse } from '@/services/municipalities'
import { isHandledAuthError } from '@/services/client'
import { applyApiFieldErrors } from '@/services'
import {
  announcementsApi,
  type AnnouncementAdmin,
  type AnnouncementType,
  type AnnouncementUpsert,
} from '@/services/admin/announcementsApi'

const TYPE_OPTIONS: Array<{ value: AnnouncementType; label: string }> = [
  { value: 'STATIC', label: '静的お知らせ' },
  { value: 'AUTO_PAYMENT_REMINDER', label: '支払期限通知' },
]

const ACTIVE_FILTER_OPTIONS: Array<{ value: 'ALL' | 'ACTIVE' | 'INACTIVE'; label: string }> = [
  { value: 'ALL', label: '有効/無効（全て）' },
  { value: 'ACTIVE', label: '有効' },
  { value: 'INACTIVE', label: '無効' },
]

// 検索条件は最後に適用した結果を保持する（顧客要望）。この画面はクライアント側で絞り込むため
// 並び順は持たず、適用済みフィルタとページ番号のみ localStorage（search_<sub>_announcement-management）へ保存する。
interface AnnouncementSearchState {
  type: AnnouncementType | 'ALL'
  municipalityId: number | 'ALL' | 'NULL'
  active: 'ALL' | 'ACTIVE' | 'INACTIVE'
  page: number
}

interface FormValues {
  type: AnnouncementType
  municipalityId: number | 'ALL'
  title: string
  body: string
  displayRange: [Dayjs | null, Dayjs | null] | null
  daysBefore: number | null
  isActive: boolean
  publishedAt: Dayjs | null
  sortOrder: number | null
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = dayjs(value)
  if (!d.isValid()) return value ?? '—'
  return d.format('YYYY-MM-DD HH:mm')
}

function typeLabel(type: AnnouncementType): string {
  return TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type
}

const requiredLabel = (label: string) => (
  <span className="inline-flex items-center gap-1">
    <span aria-hidden className="text-sm font-bold text-red-500">
      *
    </span>
    <span>{label}</span>
  </span>
)

const AnnouncementManagement = () => {
  const goBackToParent = useBackToParent()
  const toast = useToast()
  // お知らせ管理は ADMIN / SYSTEM_ADMIN いずれも利用可（仕様 2026-05-20）。
  // HOME_SYSTEM_MGMT_MENU は両ロールが保有しているため、これでガードする。
  const { hasPermission, isSystemAdmin } = usePermission()
  const { session } = useAuth()
  const canManageAnnouncements = hasPermission('HOME_SYSTEM_MGMT_MENU')
  // ADMIN は自分の自治体に強制スコープ。municipality_id IS NULL（全自治体共通）は
  // SYSTEM_ADMIN 専管のため UI からも除外する。backend (AdminAnnouncementService) は
  // 既に同じスコープルールを enforce しているのでこの変更は UX 合わせ。
  const adminMunicipalityId = !isSystemAdmin ? (session?.municipalityId ?? null) : null
  const [form] = Form.useForm<FormValues>()

  const [rows, setRows] = useState<AnnouncementAdmin[]>([])
  const [municipalities, setMunicipalities] = useState<MunicipalityResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<AnnouncementAdmin | null>(null)
  const [saving, setSaving] = useState(false)

  // ドラフト（フォーム入力中の値）
  const initialMunicipalityFilter: number | 'ALL' | 'NULL' = adminMunicipalityId ?? 'ALL'
  const [savedSearch, setSavedSearch] = useSearchPersistence<AnnouncementSearchState>('announcement-management', {
    type: 'ALL',
    municipalityId: initialMunicipalityFilter,
    active: 'ALL',
    page: 1,
  })
  // ADMIN は自治体スコープが固定のため、保存値に関わらず自身の自治体へ強制する。
  const restoredMunicipalityId: number | 'ALL' | 'NULL' =
    adminMunicipalityId != null ? initialMunicipalityFilter : savedSearch.municipalityId
  const [filterType, setFilterType] = useState<AnnouncementType | 'ALL'>(savedSearch.type)
  const [filterMunicipalityId, setFilterMunicipalityId] = useState<number | 'ALL' | 'NULL'>(restoredMunicipalityId)
  const [filterActive, setFilterActive] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>(savedSearch.active)
  // 適用済み条件（検索ボタン押下で確定）
  const [applied, setApplied] = useState<{
    type: AnnouncementType | 'ALL'
    municipalityId: number | 'ALL' | 'NULL'
    active: 'ALL' | 'ACTIVE' | 'INACTIVE'
  }>({ type: savedSearch.type, municipalityId: restoredMunicipalityId, active: savedSearch.active })
  const [page, setPage] = useState(savedSearch.page ?? 1)
  const [pageSize, setPageSize] = usePersistentPageSize('announcement-management')

  const runSearch = () => {
    setApplied({ type: filterType, municipalityId: filterMunicipalityId, active: filterActive })
    setPage(1)
  }
  const runClear = () => {
    setFilterType('ALL')
    setFilterMunicipalityId(initialMunicipalityFilter)
    setFilterActive('ALL')
    setApplied({ type: 'ALL', municipalityId: initialMunicipalityFilter, active: 'ALL' })
    setPage(1)
  }

  const watchType = Form.useWatch('type', form)

  const municipalityNameMap = useMemo(() => {
    const m = new Map<number, string>()
    municipalities.forEach((mu) => m.set(mu.id, mu.name))
    return m
  }, [municipalities])

  const loadRows = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await announcementsApi.list()
      setRows(data)
    } catch (error) {
      if (!isHandledAuthError(error)) {
        setRows([])
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!canManageAnnouncements) return
    let cancelled = false
    void (async () => {
      try {
        const { data } = await municipalitiesApi.list()
        if (!cancelled) setMunicipalities(data)
      } catch {
        if (!cancelled) setMunicipalities([])
      }
    })()
    // 一覧データを取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadRows()
    return () => {
      cancelled = true
    }
  }, [canManageAnnouncements, loadRows])

  // 適用済みフィルタとページ番号を保存し、再訪問時に前回の検索結果を復元する。
  useEffect(() => {
    setSavedSearch({ ...applied, page })
  }, [applied, page, setSavedSearch])

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (applied.type !== 'ALL' && r.type !== applied.type) return false
      if (applied.municipalityId === 'NULL' && r.municipalityId !== null) return false
      if (
        typeof applied.municipalityId === 'number' &&
        r.municipalityId !== applied.municipalityId
      ) return false
      if (applied.active === 'ACTIVE' && !r.isActive) return false
      if (applied.active === 'INACTIVE' && r.isActive) return false
      return true
    })
  }, [rows, applied])

  const { totalPages, safePage, rangeFrom, rangeTo } = computePagination(
    page,
    filteredRows.length,
    pageSize,
  )
  const pageRows = useMemo(
    () => filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filteredRows, safePage, pageSize],
  )

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      type: 'STATIC',
      municipalityId: adminMunicipalityId ?? 'ALL',
      title: '',
      body: '',
      displayRange: undefined,
      daysBefore: null,
      isActive: true,
      publishedAt: dayjs(),
      sortOrder: null,
    })
    setModalOpen(true)
  }

  const openEdit = (row: AnnouncementAdmin) => {
    setEditing(row)
    form.resetFields()
    form.setFieldsValue({
      type: row.type,
      municipalityId: row.municipalityId ?? 'ALL',
      title: row.title,
      body: row.body,
      displayRange: [
        row.displayStartAt ? dayjs(row.displayStartAt) : null,
        row.displayEndAt ? dayjs(row.displayEndAt) : null,
      ],
      daysBefore: row.filterConfig?.daysBefore ?? null,
      isActive: row.isActive,
      publishedAt: row.publishedAt ? dayjs(row.publishedAt) : null,
      sortOrder: row.sortOrder,
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    if (saving) return
    setModalOpen(false)
    setEditing(null)
    form.resetFields()
  }

  const handleSubmit = async () => {
    let values: FormValues
    try {
      values = await form.validateFields()
    } catch (validationError: unknown) {
      if (validationError && typeof validationError === 'object' && 'errorFields' in validationError) return
      Modal.error({ content: '入力内容を確認してください。' })
      return
    }

    // DatePicker.RangePicker は antd 標準で開始 <= 終了の順序を保証するため、
    // ここでの大小チェックは不要（W2: 重複バリデーションを除去）。
    const range = values.displayRange ?? [null, null]
    const start = range[0]
    const end = range[1]

    const payload: AnnouncementUpsert = {
      type: values.type,
      title: values.title.trim(),
      body: values.body,
      municipalityId:
        values.municipalityId === 'ALL' ? null : Number(values.municipalityId),
      displayStartAt: start ? start.format('YYYY-MM-DDTHH:mm:ss') : null,
      displayEndAt: end ? end.format('YYYY-MM-DDTHH:mm:ss') : null,
      filterConfig:
        values.type === 'AUTO_PAYMENT_REMINDER' && values.daysBefore != null
          ? { daysBefore: values.daysBefore }
          : null,
      isActive: values.isActive,
      publishedAt: (values.publishedAt ?? dayjs()).format('YYYY-MM-DD'),
      sortOrder: values.sortOrder,
    }

    setSaving(true)
    try {
      if (editing) {
        await announcementsApi.update(editing.id, payload)
        toast.success('お知らせを更新しました。')
      } else {
        await announcementsApi.create(payload)
        toast.success('お知らせを作成しました。')
      }
      setModalOpen(false)
      setEditing(null)
      form.resetFields()
      await loadRows()
    } catch (error) {
      if (isHandledAuthError(error)) return
      // フィールド単位エラー（400 + fieldErrors）は Form の各 Item にメッセージを反映。
      // それ以外の API エラーは axios interceptor が SystemErrorDialog で表示する。
      applyApiFieldErrors(form, error)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = (row: AnnouncementAdmin) => {
    Modal.confirm({
      title: 'お知らせを削除しますか？',
      content: `「${row.title}」を削除します。この操作は取り消せません。`,
      okText: '削除',
      okButtonProps: { danger: true },
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          await announcementsApi.remove(row.id)
          toast.success('お知らせを削除しました。')
          await loadRows()
        } catch (error) {
          if (isHandledAuthError(error)) return
        }
      },
    })
  }

  if (!canManageAnnouncements) {
    return (
      <div className="min-h-screen bg-gray-100">
        <SystemHeader
          breadcrumbs={[
            { label: 'システム管理', page: 'submenu-admin' },
            { label: 'お知らせ管理' },
          ]}
        />
        <Result status="403" title="権限がありません" subTitle="お知らせ管理は管理者・システム管理者のみ利用できます。" />
      </div>
    )
  }

  const columns: ColumnsType<AnnouncementAdmin> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: '種類',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (t: AnnouncementType) => (
        <Tag color={t === 'AUTO_PAYMENT_REMINDER' ? 'orange' : 'blue'}>{typeLabel(t)}</Tag>
      ),
    },
    {
      title: 'タイトル',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span className="block truncate" style={{ maxWidth: 280 }}>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: '自治体',
      dataIndex: 'municipalityId',
      key: 'municipalityId',
      width: 200,
      render: (id: number | null) =>
        id == null ? <Tag color="default">全自治体</Tag> : (municipalityNameMap.get(id) ?? `#${id}`),
    },
    {
      title: '表示開始',
      dataIndex: 'displayStartAt',
      key: 'displayStartAt',
      width: 160,
      render: formatDateTime,
    },
    {
      title: '表示終了',
      dataIndex: 'displayEndAt',
      key: 'displayEndAt',
      width: 160,
      render: formatDateTime,
    },
    {
      title: '状態',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (active: boolean) =>
        active ? <Tag color="green">有効</Tag> : <Tag color="default">無効</Tag>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_: unknown, row: AnnouncementAdmin) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            編集
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(row)}
          >
            削除
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={[
          { label: 'システム管理', page: 'submenu-admin' },
          { label: 'お知らせ管理' },
        ]}
      />
      <div className="mx-auto w-full max-w-[1600px] px-6 py-8">
        <BackButton onClick={goBackToParent} />

        <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-gray-700">
          ホーム画面に表示するお知らせを管理します。自治体別／全自治体共通の両方を登録できます。
        </div>

        <SearchCard className="mb-6">
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            <DemoSelect
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as AnnouncementType | 'ALL')}
            >
              <option value="ALL">種類（全て）</option>
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </DemoSelect>
            <DemoSelect
              value={String(filterMunicipalityId)}
              onChange={(e) => {
                const v = e.target.value
                if (v === 'ALL' || v === 'NULL') setFilterMunicipalityId(v)
                else setFilterMunicipalityId(Number(v))
              }}
              disabled={adminMunicipalityId != null}
            >
              {adminMunicipalityId == null && (
                <>
                  <option value="ALL">自治体（全て）</option>
                  <option value="NULL">全自治体共通のみ</option>
                </>
              )}
              {municipalities
                .filter((m) => adminMunicipalityId == null || m.id === adminMunicipalityId)
                .map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
            </DemoSelect>
            <DemoSelect
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value as 'ALL' | 'ACTIVE' | 'INACTIVE')}
            >
              {ACTIVE_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </DemoSelect>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={runClear} className={demoButtonClass.secondary}>
              クリア
            </button>
            <button type="button" onClick={runSearch} className={demoButtonClass.primary}>
              検索
            </button>
          </div>
        </SearchCard>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button type="button" onClick={openCreate} className={demoButtonClass.primarySm}>
            新規作成
          </button>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-lg">
          <Table<AnnouncementAdmin>
            rowKey="id"
            loading={loading}
            dataSource={pageRows}
            columns={columns}
            pagination={false}
            scroll={{ x: 1200 }}
            locale={{ emptyText: '該当するお知らせがありません' }}
          />
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            total={filteredRows.length}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onPageChange={(next) => setPage(next)}
            pageSize={pageSize}
            onPageSizeChange={(next) => { setPageSize(next); setPage(1) }}
          />
        </div>
      </div>

      <Modal
        title={editing ? 'お知らせを編集' : 'お知らせを新規作成'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText="保存"
        cancelText="キャンセル"
        width={680}
        destroyOnHidden
        maskClosable={false}
      >
        <Form<FormValues> form={form} layout="vertical" requiredMark={false} className="customer-validation-form mt-2">
          <Form.Item
            name="type"
            label={requiredLabel('種類')}
            rules={[{ required: true, message: '種類を選択してください。' }]}
          >
            <Select options={TYPE_OPTIONS} />
          </Form.Item>

          <Form.Item
            name="municipalityId"
            label={requiredLabel('自治体')}
            dependencies={['type']}
            rules={[
              { required: true, message: '自治体を選択してください。' },
              {
                validator: (_, value) => {
                  // 支払期限通知 (Type①) は自治体スコープ必須。「全自治体共通」では countUpcomingUnfinishedScoped
                  // の municipalityId が null 扱いとなり集計が不定になるため禁止。
                  if (watchType === 'AUTO_PAYMENT_REMINDER' && value === 'ALL') {
                    return Promise.reject(new Error('支払期限通知は自治体を指定してください。'))
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              disabled={adminMunicipalityId != null}
              options={
                adminMunicipalityId != null
                  ? municipalities
                      .filter((m) => m.id === adminMunicipalityId)
                      .map((m) => ({ value: m.id, label: m.name }))
                  : [
                      { value: 'ALL', label: '全自治体共通' },
                      ...municipalities.map((m) => ({ value: m.id, label: m.name })),
                    ]
              }
            />
          </Form.Item>

          <Form.Item
            name="title"
            label={requiredLabel('タイトル')}
            rules={[
              { required: true, message: 'タイトルは必須です。' },
              { max: 256, message: 'タイトルは256文字以内で入力してください。' },
            ]}
          >
            <Input maxLength={256} />
          </Form.Item>

          <Form.Item
            name="body"
            label={
              <span className="inline-flex items-center gap-1">
                {requiredLabel('本文')}
                <Tooltip title="種類が「支払期限通知」のとき、本文中の {days} と {count} はそれぞれ閾値日数・件数に展開されます。">
                  <QuestionCircleOutlined className="text-gray-400" />
                </Tooltip>
              </span>
            }
            rules={[{ required: true, message: '本文は必須です。' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item name="displayRange" label="表示期間（開始 〜 終了）">
            <DatePicker.RangePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>

          {watchType === 'AUTO_PAYMENT_REMINDER' && (
            <Form.Item
              name="daysBefore"
              label={requiredLabel('振込予定日まで（日数）')}
              rules={[
                { required: true, message: '日数を入力してください。' },
                {
                  validator: (_, value) => {
                    if (value == null) return Promise.resolve()
                    if (typeof value !== 'number' || value < 1 || value > 365) {
                      return Promise.reject(new Error('1〜365 の範囲で入力してください。'))
                    }
                    return Promise.resolve()
                  },
                },
              ]}
            >
              <InputNumber min={1} max={365} style={{ width: 160 }} />
            </Form.Item>
          )}

          <Form.Item name="isActive" label="有効" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            name="publishedAt"
            label={requiredLabel('公開日')}
            rules={[{ required: true, message: '公開日を選択してください。' }]}
          >
            <DatePicker style={{ width: 200 }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item name="sortOrder" label="並び順（任意）">
            <InputNumber style={{ width: 160 }} placeholder="未指定なら公開日順" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default AnnouncementManagement
